import { useState, useRef } from 'react'
import { Sparkles, Zap, Bell, Shield, FileUp } from 'lucide-react'
import { useSettings } from '../stores/useSettings'
import { useAuth } from '../stores/useAuth'
import { THEMES } from '../constants/themes'
import type { ThemeName } from '../constants/themes'
import { supabase } from '../services/supabase'
import { getApiUrl } from '../services/api'

interface SectionProps {
  title: string;
  icon: React.ComponentType<{ size?: number }>;
  children: React.ReactNode;
}

function Section({ title, icon: Icon, children }: SectionProps) {
  return (
    <div style={{ marginBottom: 'var(--xxl)' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--md)' }}>
        <span className="text-accent" style={{ marginRight: 'var(--sm)', display: 'inline-flex', alignItems: 'center' }}>
          <Icon size={18} />
        </span>
        <span className="label text-muted">{title}</span>
      </div>
      <div className="card card-low" style={{ padding: 'var(--md)' }}>
        {children}
      </div>
    </div>
  )
}

interface SettingRowProps {
  label: string;
  children: React.ReactNode;
}

function SettingRow({ label, children }: SettingRowProps) {
  return (
    <div className="settings-row" style={{ margin: 'var(--sm) 0' }}>
      <span className="body text-primary">{label}</span>
      {children}
    </div>
  )
}

interface TimePickerProps {
  value: string;
  onChange: (v: string) => void;
}

function TimePicker({ value, onChange }: TimePickerProps) {
  const [h, m] = value.split(':').map(Number)
  const hour = isNaN(h) ? 9 : h
  const minute = isNaN(m) ? 0 : m

  const nudgeHour = (delta: number) => {
    const next = ((hour + delta + 24) % 24).toString().padStart(2, '0')
    onChange(`${next}:${minute.toString().padStart(2, '0')}`)
  }

  const nudgeMin = (delta: number) => {
    const next = ((minute + delta + 60) % 60).toString().padStart(2, '0')
    onChange(`${hour.toString().padStart(2, '0')}:${next}`)
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--md)', padding: 'var(--sm) 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <button
          onClick={() => nudgeHour(1)}
          className="btn btn-ghost btn-sm btn-icon"
          style={{ width: '32px', height: '32px' }}
        >
          ▲
        </button>
        <span className="mono text-primary" style={{ fontSize: '32px', lineHeight: '40px', fontWeight: 'bold' }}>
          {hour.toString().padStart(2, '0')}
        </span>
        <button
          onClick={() => nudgeHour(-1)}
          className="btn btn-ghost btn-sm btn-icon"
          style={{ width: '32px', height: '32px' }}
        >
          ▼
        </button>
      </div>

      <span className="mono text-muted" style={{ fontSize: '28px' }}>:</span>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <button
          onClick={() => nudgeMin(5)}
          className="btn btn-ghost btn-sm btn-icon"
          style={{ width: '32px', height: '32px' }}
        >
          ▲
        </button>
        <span className="mono text-primary" style={{ fontSize: '32px', lineHeight: '40px', fontWeight: 'bold' }}>
          {minute.toString().padStart(2, '0')}
        </span>
        <button
          onClick={() => nudgeMin(-5)}
          className="btn btn-ghost btn-sm btn-icon"
          style={{ width: '32px', height: '32px' }}
        >
          ▼
        </button>
      </div>

      <span className="body-md text-dim" style={{ marginLeft: 'var(--sm)' }}>
        {hour < 12 ? 'AM' : 'PM'}
      </span>
    </div>
  )
}

export default function SettingsPage() {
  const { settings, update } = useSettings()
  const { signOut, user } = useAuth()
  const [extracting, setExtracting] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle')
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const save = async (patch: Parameters<typeof update>[0]) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    try {
      await update(patch)
      setSaveStatus('saved')
    } catch {
      setSaveStatus('error')
    }
    saveTimer.current = setTimeout(() => setSaveStatus('idle'), 2000)
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setExtracting(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const formData = new FormData()
      formData.append('document', file)

      const response = await fetch(`${getApiUrl()}/api/extract-goals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token ?? ''}`,
        },
        body: formData,
      })

      if (!response.ok) throw new Error('Extraction failed')
      const data = await response.json()
      await save({ ai_context: data.goals })
    } catch {
      alert('Failed to extract goals from document.')
    } finally {
      setExtracting(false)
    }
  }

  return (
    <div className="page page-top animate-fade-slide-up" style={{ paddingBottom: '100px' }}>
      {/* Save status toast */}
      {saveStatus !== 'idle' && (
        <div
          className={`toast ${saveStatus === 'saved' ? 'toast-success' : 'toast-error'}`}
          style={{ position: 'fixed', top: '24px' }}
        >
          {saveStatus === 'saved' ? '✓ Saved' : '✕ Save failed'}
        </div>
      )}

      <h1 className="display-md text-primary" style={{ marginBottom: 'var(--xl)' }}>Settings</h1>

      {/* Appearance */}
      <Section title="Appearance" icon={Sparkles}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sm)' }}>
          {(Object.keys(THEMES) as ThemeName[]).map((name) => (
            <button
              key={name}
              onClick={() => save({ theme: name })}
              style={{
                width: '52px',
                height: '52px',
                borderRadius: 'var(--r-full)',
                backgroundColor: THEMES[name].bg,
                border: `2px solid ${settings.theme === name ? THEMES[name].accent : THEMES[name].border}`,
                cursor: 'pointer',
                position: 'relative',
                transition: 'all var(--transition)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={THEMES[name].label}
            >
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: 'var(--r-full)',
                  backgroundColor: THEMES[name].accent,
                  position: 'absolute',
                  bottom: '2px',
                  right: '2px',
                }}
              />
            </button>
          ))}
        </div>
      </Section>

      {/* Focus Parameters */}
      <Section title="Focus Parameters" icon={Zap}>
        <div style={{ marginBottom: 'var(--md)' }}>
          <SettingRow label="Signal Slots">
            <span className="title text-accent">{settings.signal_slots}</span>
          </SettingRow>
          <input
            type="range"
            min={1}
            max={10}
            step={1}
            value={settings.signal_slots}
            onChange={(e) => save({ signal_slots: Number(e.target.value) })}
          />
        </div>
        <div style={{ marginBottom: 'var(--md)' }}>
          <SettingRow label="Noise Budget (mins)">
            <span className="title text-accent">{settings.noise_budget_mins}</span>
          </SettingRow>
          <input
            type="range"
            min={15}
            max={120}
            step={5}
            value={settings.noise_budget_mins}
            onChange={(e) => save({ noise_budget_mins: Number(e.target.value) })}
          />
        </div>
      </Section>

      {/* Notifications */}
      <Section title="Notifications" icon={Bell}>
        <SettingRow label="Daily Reminder">
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.daily_reminder_enabled}
              onChange={(e) => save({ daily_reminder_enabled: e.target.checked })}
            />
            <span className="toggle-slider"></span>
          </label>
        </SettingRow>

        {settings.daily_reminder_enabled && (
          <div className="animate-fade-slide-up" style={{ marginTop: 'var(--sm)' }}>
            <p className="body-md text-muted" style={{ marginBottom: 'var(--md)', lineHeight: '1.4' }}>
              You'll be notified at this time each day. If you haven't logged any tasks yet, Loqin will keep nudging you every 30 minutes until you do.
            </p>
            <TimePicker
              value={settings.daily_reminder_time}
              onChange={(v) => save({ daily_reminder_time: v })}
            />
          </div>
        )}
      </Section>

      {/* AI Engine */}
      <Section title="AI Engine" icon={Shield}>
        <SettingRow label="Auto-classification">
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.ai_classification}
              onChange={(e) => save({ ai_classification: e.target.checked })}
            />
            <span className="toggle-slider"></span>
          </label>
        </SettingRow>

        <div style={{ marginTop: 'var(--md)', marginBottom: 'var(--sm)' }}>
          <span className="label text-dim">API Provider</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sm)' }}>
          {(['anthropic', 'openai', 'gemini', 'groq'] as const).map((p) => (
            <button
              key={p}
              onClick={() => save({ ai_provider: p })}
              className={`btn btn-sm ${settings.ai_provider === p ? 'btn-primary' : 'btn-outline'}`}
              style={{ width: 'auto', padding: '6px 16px', borderRadius: 'var(--r-md)' }}
            >
              {p}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 'var(--md)', marginBottom: 'var(--sm)' }}>
          <span className="label text-dim">Coaching Tone</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sm)' }}>
          {([
            { value: 'mentor',  label: 'Mentor',        emoji: '🧑‍🏫' },
            { value: 'stoic',   label: 'Stoic',         emoji: '🪨' },
            { value: 'drill',   label: 'Drill Sergeant', emoji: '💪' },
            { value: 'hype',    label: 'Hype',          emoji: '🔥' },
          ] as const).map(({ value, label, emoji }) => (
            <button
              key={value}
              onClick={() => save({ coaching_tone: value })}
              className={`btn btn-sm ${settings.coaching_tone === value ? 'btn-primary' : 'btn-outline'}`}
              style={{ width: 'auto', padding: '6px 14px', borderRadius: 'var(--r-md)' }}
            >
              {emoji} {label}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 'var(--md)', marginBottom: 'var(--sm)' }}>
          <span className="label text-dim">BYOK API Key</span>
        </div>
        <input
          type="password"
          className="input"
          value={settings.user_api_key}
          onChange={(e) => update({ user_api_key: e.target.value })}
          onBlur={() => save({ user_api_key: settings.user_api_key })}
          placeholder="sk-••••••••••••"
        />
      </Section>

      {/* Goals & Context */}
      <Section title="Goals & Context" icon={FileUp}>
        <p className="body-md text-muted" style={{ marginBottom: 'var(--md)', lineHeight: '1.4' }}>
          Upload a PDF or TXT document of your goals to help the AI categorize your tasks and provide better coaching.
        </p>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.txt"
          style={{ display: 'none' }}
        />

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={extracting}
          className="btn btn-outline"
          style={{ marginBottom: 'var(--md)' }}
        >
          {extracting ? 'Extracting...' : 'Upload Goals Doc'}
        </button>

        <div style={{ marginTop: 'var(--md)', marginBottom: 'var(--sm)' }}>
          <span className="label text-dim">About you (AI Context)</span>
        </div>
        <textarea
          className="input textarea"
          value={settings.ai_context}
          onChange={(e) => update({ ai_context: e.target.value })}
          onBlur={() => save({ ai_context: settings.ai_context })}
          placeholder="I'm a solo SaaS founder building..."
        />
      </Section>

      {/* Account */}
      <Section title="Account" icon={Bell}>
        <div className="settings-row">
          <span className="body text-primary">Logged in as</span>
          <span className="body-md text-muted">{user?.email}</span>
        </div>
        <button
          onClick={signOut}
          className="btn btn-outline"
          style={{ marginTop: 'var(--md)', color: 'var(--noise)', borderColor: 'var(--noise)' }}
        >
          Sign Out
        </button>
      </Section>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--xl)' }}>
        <span className="label text-dim">Loqin — Version 2.0.0</span>
      </div>
    </div>
  )
}
